import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

import Sidenav from "examples/Sidenav";
import Configurator from "examples/Configurator";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import Login from "layouts/login";
import { RequireAuth, useAuth } from "auth/AuthContext";
import { canOpenRoute, homeRoute, visibleRoutes } from "auth/access";
import { FeedbackProvider } from "components/admin/Feedback";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController, setMiniSidenav } from "context";

import brandWhite from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

/** Trang không được vào thì chuyển về trang đầu tiên được vào. */
function RouteGuard({ route, children }) {
  const { admin, logout } = useAuth();
  if (canOpenRoute(admin, route)) {
    return children;
  }
  const home = homeRoute(routes, admin);
  if (home) {
    return <Navigate to={home} replace />;
  }
  return (
    <MDBox
      display="flex"
      flexDirection="column"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
    >
      <MDTypography variant="button" color="error" fontWeight="medium">
        Tài khoản này chưa được cấp quyền trang nào
      </MDTypography>
      <MDButton variant="outlined" color="info" size="small" sx={{ mt: 2 }} onClick={logout}>
        Đăng xuất
      </MDButton>
    </MDBox>
  );
}

RouteGuard.propTypes = {
  route: PropTypes.shape({ permission: PropTypes.string }).isRequired,
  children: PropTypes.node.isRequired,
};

export default function App() {
  const { admin } = useAuth();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, layout, sidenavColor, transparentSidenav, whiteSidenav, darkMode } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const isLogin = pathname === "/login";

  return (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      <FeedbackProvider>
        {layout === "dashboard" && !isLogin && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={(transparentSidenav && !darkMode) || whiteSidenav ? brandDark : brandWhite}
              brandName="Pet Realm Admin"
              routes={visibleRoutes(routes, admin)}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            <Configurator />
          </>
        )}
        <Routes>
          <Route path="/login" element={<Login />} />
          {routes
            .filter((route) => route.route)
            .map((route) => (
              <Route
                key={route.key}
                path={route.route}
                element={
                  <RequireAuth>
                    <RouteGuard route={route}>{route.component}</RouteGuard>
                  </RequireAuth>
                }
              />
            ))}
          <Route
            path="*"
            element={<Navigate to={homeRoute(routes, admin) || "/dashboard"} replace />}
          />
        </Routes>
      </FeedbackProvider>
    </ThemeProvider>
  );
}
